import Tracker from './Tracker';
import type { initTracker } from './Tracker';

interface ComscoreConfig {
  encodedGuid?: string;
  encodedZuid?: string;
  id: string;
}

interface ComscoreParams {
  c1: string;
  c2: string;
  c3: string;
  c4: string;
  c5: string;
  c6: string;
  c15: string;
  cs_fpdm: string;
  cs_fpdt: string;
  cs_fpid: string;
  cs_fpit: 'li' | 'lo' | '*null'; // Logged in or logged out
  options: {
    url_append?: 'comscorekw=rentals';
  };
}

export default class Comscore extends Tracker {
  private config: ComscoreConfig;

  constructor(args: initTracker & ComscoreConfig) {
    super({ ...args, type: 'comscore' });
    this.config = {
      id: args.id,
      encodedZuid: args.encodedZuid,
      encodedGuid: args.encodedGuid,
    };
  }

  private getComscoreParams(): ComscoreParams {
    const { id, encodedZuid, encodedGuid } = this.config;
    const isLoggedIn = Boolean(encodedZuid);
    const pageUrl = typeof window !== 'undefined' && window.location ? window.location.href : '*null';
    const uid = isLoggedIn ? encodedZuid : encodedGuid;

    const params: ComscoreParams = {
      c1: '2',
      c2: id,
      c3: '*null',
      c4: pageUrl || '*null',
      c5: '*null',
      c6: '*null',
      c15: '*null',
      cs_fpdm: '*null',
      cs_fpdt: '*null',
      cs_fpid: uid || '*null',
      cs_fpit: encodedGuid ? 'lo' : isLoggedIn ? 'li' : '*null',
      options: {
        url_append: 'comscorekw=rentals',
      },
    };

    return params;
  }

  public updateConfigWithZuid(config: { encodedZuid?: string; encodedGuid?: string }) {
    this.config = { ...this.config, ...config };
  }

  /**
   * Renders the Comscore HTML for server-side rendering (SSR) apps that also call `trackPageView`.
   * This method is used to inject window.COMSCORE.beacon into the HTML.
   */
  renderHtmlInBody = () => {
    return `
      <script type="text/plain" class="optanon-category-3" async>
        var _comscore;
        var _comscoreBackup;
        var s;
        var el;

        if (typeof window !== 'undefined' && !window._comscore) {
          _comscore = _comscore || [];
          _comscoreBackup = _comscoreBackup || [];
          window._comscore = _comscore;
          window._comscoreBackup = _comscoreBackup;

          var getComscoreParams = () => {
            const { id, encodedZuid, encodedGuid } = ${JSON.stringify(this.config)};
            const isLoggedIn = Boolean(encodedZuid);
            const pageUrl = window.location.href;
            const uid = isLoggedIn ? encodedZuid : encodedGuid;

            return {
              c1: '2',
              c2: id,
              c3: '*null',
              c4: pageUrl,
              c5: '*null',
              c6: '*null',
              c15: '*null',
              cs_fpdm: '*null',
              cs_fpdt: '*null',
              cs_fpid: uid || '*null',
              cs_fpit: encodedGuid ? 'lo' : isLoggedIn ? 'li' : '*null',
              options: {
                url_append: 'comscorekw=rentals',
              },
            };
          };

          var comscoreParams = getComscoreParams();
          window._comscore.push(comscoreParams);
          window._comscoreBackup.push.apply(window._comscoreBackup, window._comscore);
        }
      </script>
      <script type="text/plain" src="https://sb.scorecardresearch.com/beacon.js" async class="optanon-category-3"></script>
    `;
  };

  /**
   * Note: This method is intended for use in client-side rendering (CSR) apps, where the Comscore HTML is generated on the client.
   * In server-side rendering (SSR) apps, use `renderHtmlInBody` instead.
   */
  renderComscoreHtml(): string {
    // Return the script code without the script tag
    const params = this.getComscoreParams();
    return `
      if (typeof window !== 'undefined' && !window._comscore) {
        var _comscore = _comscore || [];
        var _comscoreBackup = _comscoreBackup || [];
        
        window._comscore = window._comscore || [];
        window._comscoreBackup = window._comscoreBackup || [];

        (function() {
          var comscoreParams = ${JSON.stringify(params)};
          window._comscore.push(comscoreParams);
          window._comscoreBackup.push.apply(window._comscoreBackup, window._comscore);

          const cs = document.createElement('script');
          cs.type = 'text/javascript';
          cs.setAttribute('async', true);
          cs.setAttribute('class', 'optanon-category-3');
          cs.src = "https://sb.scorecardresearch.com/cs/${this.config.id}/beacon.js";
          const el = document.getElementsByTagName('script')[0];
          el.parentNode.insertBefore(cs, el);
        }());
      }
    `;
  }

  trackPageView = (_: unknown, cb: (s: string) => void) => {
    this.logger &&
      this.logger.debug(
        {
          id: this.id,
        },
        'trackPageView',
      );

    if (!this.drymode) {
      // window.COMSCORE is created by the injected script
      if (typeof window !== 'undefined' && window.COMSCORE) {
        const params = this.getComscoreParams();
        window.COMSCORE.beacon(params);

        if (cb) {
          cb(this.id);
        }
      }
    }

    return this.result(true);
  };

  trackEvent = () => {
    this.logger && this.logger.debug('comscore has no trackEvent action');
  };
}
